import DirectionsResult = google.maps.DirectionsResult;
import DirectionsOptions = google.maps.DirectionsRendererOptions;
import { QPointMapMode, QPointMapModeScope } from '../map-mode-selection/mode';
import { Component, computed, EventEmitter, inject, InjectionToken, Input, OnDestroy, Output, signal } from '@angular/core';
import { cloneDeep } from 'lodash';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MapModeStateService } from '../map-mode-selection/map-mode-state.service';
import { injectDestroyRef } from '@qpoint/utils';

export type QPointMapDirection = DirectionsResult & { scope?: QPointMapModeScope };
export type QPointMapDirectionOptions = DirectionsOptions;

export const QPOINT_DIRECTION_DEFAULT_OPTIONS = new InjectionToken<QPointMapDirectionOptions>('Polyline Options');

@Component({
  selector: 'qpoint-map-direction',
  template: '',
  standalone: true
})
export class MapDirectionComponent implements OnDestroy {
  private destroyRef = injectDestroyRef();
  private mapModeState = inject(MapModeStateService);
  private readonly _defaultOptions: QPointMapDirectionOptions = inject(QPOINT_DIRECTION_DEFAULT_OPTIONS, { optional: true }) || {
    hideRouteList: true,
    polylineOptions: {
      strokeOpacity: 1,
      zIndex: 10,
      strokeColor: '#0B91B1',
      strokeWeight: 5,
      draggable: false,
      editable: false
    },
    draggable: false,
    suppressMarkers: false
  };
  private subscription: Subscription | null = null;
  private _editMode = signal<boolean>(false);
  private _scope = signal<QPointMapModeScope>('all');
  private _options = signal<QPointMapDirectionOptions>(this._defaultOptions);
  private _direction = signal<QPointMapDirection | null>(null);
  editMode = this._editMode.asReadonly();
  scope = this._scope.asReadonly();
  direction = this._direction.asReadonly();

  options = computed(() => {
    const options = this._options();
    const editMode = this._editMode();
    const anyModeActive = this.mapModeState.anyModeActive();

    return {
      ...options,
      polylineOptions: {
        ...options.polylineOptions,
        strokeOpacity: anyModeActive && !editMode ? 0.3 : {
          ...this._defaultOptions,
        }.polylineOptions.strokeOpacity,
        zIndex: anyModeActive && !editMode ? 10 : 11
      },
      draggable: editMode
    }
  })

  @Input('direction') set directionSetter(direction: QPointMapDirection) {
    this._direction.set(cloneDeep(direction));
    this._scope.set(direction?.scope ?? 'all');
    this.setupSubscription();
  }

  @Input('options') set optionsSetter(options: QPointMapDirectionOptions) {
    this._options.set({
      ...this._defaultOptions,
      ...options,
      polylineOptions: {
        ...this._defaultOptions.polylineOptions,
        ...(options.polylineOptions ?? {})
      }
    });
  }

  @Output() directionChanged = new EventEmitter<QPointMapDirection>();

  ngOnDestroy() {
    this.subscription?.unsubscribe();
    this.subscription = null;
  }

  handleDirectionChanged(event: google.maps.DirectionsResult) {
    if (JSON.stringify(event.routes) !== JSON.stringify(this.direction().routes)) {
      this.directionChanged.emit(event);
    }
  }

  private setupSubscription() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.subscription = this.mapModeState.isModeActive$(QPointMapMode.Polyline, this.scope()).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: isActive => {
        this._editMode.set(isActive);
      }
    });
  }
}
